import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Globe, MapPin, Coffee, Activity, Heart, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import healthApi from '@/api/healthApi';

/**
 * Cultural Settings Component
 * Allows users to set their cultural region for personalized health recommendations
 */

const CulturalSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [currentSettings, setCurrentSettings] = useState(null);
  const [culturalExamples, setCulturalExamples] = useState(null);

  // Load data on mount
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch available regions
      const regionsResponse = await healthApi.getAvailableRegions();
      setRegions(regionsResponse.data.data || []);

      // Fetch current settings
      const settingsResponse = await healthApi.getCulturalSettings();
      setCurrentSettings(settingsResponse.data.data);
      setSelectedRegion(settingsResponse.data.data.user_settings.region || 'global');

      // Fetch cultural examples
      const examplesResponse = await healthApi.getCulturalExamples();
      setCulturalExamples(examplesResponse.data.data);

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading cultural data:', error);
      }
      toast.error('Failed to load cultural settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRegionChange = async (regionKey) => {
    try {
      setSaving(true);
      
      const response = await healthApi.updateCulturalSettings({
        region: regionKey
      });

      if (response.data.success) {
        setSelectedRegion(regionKey);
        setCurrentSettings(response.data.data);
        
        // Reload examples
        const examplesResponse = await healthApi.getCulturalExamples();
        setCulturalExamples(examplesResponse.data.data);

        toast.success('Cultural preferences updated! 🌍', {
          description: 'Your health recommendations will now be culturally adapted'
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating region:', error);
      }
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading cultural settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-full p-3">
              <Globe className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Cultural Preferences
          </h1>
          <p className="text-gray-600">
            Get health recommendations tailored to your culture and region
          </p>
        </div>

        {/* Current Region Info */}
        {culturalExamples && (
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardContent className="pt-5">
              <div className="flex items-start gap-3">
                <MapPin className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 mb-1">
                    Current Region: {culturalExamples.region_name}
                  </p>
                  <p className="text-sm text-blue-800">
                    Your health insights are personalized for this region
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Region Selection */}
        <Card className="border-2 border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg">Choose Your Region</CardTitle>
            <p className="text-sm text-gray-600">
              Select the region that best matches your cultural background
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {regions.map((region) => {
                const isSelected = selectedRegion === region.key;
                
                return (
                  <button
                    key={region.key}
                    onClick={() => handleRegionChange(region.key)}
                    disabled={saving}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-1">
                          {region.name}
                        </p>
                        <p className="text-xs text-gray-600">
                          {region.countries.slice(0, 3).join(', ')}
                          {region.countries.length > 3 && ` +${region.countries.length - 3} more`}
                        </p>
                      </div>
                      {isSelected && (
                        <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Cultural Examples */}
        {culturalExamples && (
          <>
            {/* Food Examples */}
            <Card className="border-2 border-orange-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Coffee className="h-5 w-5 text-orange-600" />
                  Foods & Beverages from Your Region
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Staple Foods:</p>
                  <div className="flex flex-wrap gap-2">
                    {culturalExamples.examples.food_staples?.map((food, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm"
                      >
                        {food}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Beverages:</p>
                  <div className="flex flex-wrap gap-2">
                    {culturalExamples.examples.beverages?.map((beverage, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {beverage}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Activities */}
            <Card className="border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5 text-green-600" />
                  Popular Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {culturalExamples.examples.activities?.map((activity, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                    >
                      {activity}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Wellness Practices */}
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Heart className="h-5 w-5 text-purple-600" />
                  Traditional Wellness Practices
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {culturalExamples.examples.wellness_practices?.map((practice, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                    >
                      {practice}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Info Card */}
        <Card className="border-2 border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50">
          <CardContent className="pt-5">
            <div className="space-y-2 text-sm text-gray-700">
              <p className="font-semibold text-yellow-900">
                ℹ️ How Cultural Preferences Work
              </p>
              <ul className="space-y-1 ml-4 list-disc text-yellow-800">
                <li>Health insights will use examples from your region</li>
                <li>Food suggestions will include culturally familiar options</li>
                <li>Activity recommendations will match local preferences</li>
                <li>All advice remains medically sound and evidence-based</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex-1 border-2 border-gray-300 hover:bg-gray-50"
          >
            Back to Home
          </Button>
          <Button
            onClick={() => navigate('/daily-loop')}
            variant="outline"
            className="flex-1 border-2 border-purple-300 hover:bg-purple-50"
          >
            See Personalized Insights
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CulturalSettings;
